/**
 * The hideout, client side (WP-X4, docs/HIDEOUT.md): the server's `hideout` view for the book's "Hideout" page and
 * the room (src/hideout/hideoutSystem.ts), refreshed by the server's pushes and by a `hideoutView` request when the
 * page is opened. Nothing here decides anything; it only keeps what the server said.
 */
import type { HideoutView } from '../../shared/messages/hideout';
import type { OnlineGame } from './game';

/** Seconds between `hideoutView` requests at most. */
const ASK_GAP_S = 5;

type Listener = (v: HideoutView | null) => void;

/** Who wants to hear of a new view (the room): kept across sign-ins, so a system can listen from its start. */
const listeners = new Set<Listener>();

/** The last view heard (null: not in a gang, or not heard yet) — read by the book feed and the room. */
export const hideoutState: { view: HideoutView | null; ask: () => void; listen: (fn: Listener) => () => void } = {
  view: null,
  ask: () => undefined,
  listen: (fn) => {
    listeners.add(fn);
    return () => void listeners.delete(fn);
  },
};

export function registerHideoutUi(game: OnlineGame): () => void {
  const book = () => game.ctx.services.tryGet('book');
  let clock = 0;
  let askedAt = -Infinity;

  const set = (v: HideoutView | null): void => {
    hideoutState.view = v;
    for (const fn of listeners) fn(v);
    if (book()?.isOpen) book()?.refresh();
  };
  const offs = [
    game.onMessage('hideout', (d) => set((d as { hideout: HideoutView | null }).hideout ?? null)),
    game.onFrame((dt) => {
      clock += dt;
    }),
  ];
  hideoutState.ask = () => {
    if (!game.store.state.ready || clock - askedAt < ASK_GAP_S) return;
    askedAt = clock;
    void game.request<{ hideout: HideoutView | null }>('hideoutView', {}, { quiet: true }).then((r) => {
      if (r.ok && r.data) set(r.data.hideout ?? null);
    });
  };
  return () => {
    for (const off of offs) off();
    set(null);
    hideoutState.ask = () => undefined;
  };
}
